
import { computed, onMounted, ref, watch } from 'vue';
import Button from 'primevue/button';
import Message from 'primevue/message';
import { game } from '../apis/game';
import { GamesList, GameStatusResult, ListByGameCodeResult, ListByGameTypeResult } from '../types/game';
import GameNavbar from '../components/navbar/GameNavbar.vue'

import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import { useAuthStore } from '../stores/auth';
import { storeToRefs } from 'pinia';
import { showVuePopup } from '../utils/showVuePopup';
import Login from '../popup/Login.vue';
import { useToast } from 'primevue/usetoast';
import { openGameUrl } from '../utils/openGameUrl';
import { isHiddenProduct } from '../utils/site-config';


export default {
	name: 'Game',
	components: {
		IconField,
		InputIcon,
		InputText,
		Button,
		Message,
		GameNavbar,
	},
	props: {
		gameType: {
			type: String,
			default: '1',
		},
	},
	setup(props) {
		const auth = useAuthStore();
		auth.hydrate();

		const toast = useToast();


		const gameType = ref(parseInt(props.gameType));
		const error = ref<string | null>(null);

		const loadingProducts = ref(true);
		const loadingGames = ref(false);

		const gameState = ref<GameStatusResult>({
			card: false,
			casino: false,
			cock: false,
			esport: false,
			fish: false,
			keno: false,
			lotto: false,
			poker: false,
			slot: false,
			sport: false,
			trade: false,
		});

		const selectedProduct = ref<GamesList>();
		const selectedGame = ref<GamesList>();
		const changeProductToGame = ref(true);
		const products = ref<GamesList[]>([]);
		const games = ref<GamesList[]>([]);

		const searchQuery = ref('');
		const searchResults = ref<GamesList[]>([])
		let searchTimer: number | undefined

		const productsList = computed(() => products.value);
		const gamesList = computed(() => {
			if (searchQuery.value.trim()) {
				return searchResults.value
			}

			return games.value
		})

		const searchProviders = async (q: string) => {
			if (!q.trim()) {
				searchResults.value = []
				return
			}
			try {
				loadingProducts.value = true;
				loadingGames.value = true
				error.value = null

				const payload = { gameType: 0, showGames: true }
				const resp = await game.listByType(payload)

				if (resp.code === '9902' || resp.code === '8017') {
					await auth.logout()
					window.location.reload()
					return
				}

				if (resp.code !== '0') throw new Error(resp.message)

				const result = resp.result as ListByGameTypeResult

				const games: GamesList[] = []
				result.gamesList.forEach(item => {
					if (isHiddenProduct(item)) return

					if (item.games && Array.isArray(item.games)) {
						games.push(...item.games.filter(g => !isHiddenProduct(g)))
					}
				})

				searchResults.value = games.filter(g =>
					g.gameName?.toLowerCase().includes(q.toLowerCase())
				)

				changeProductToGame.value = false
			} catch (err: any) {
				error.value = err.message
			} finally {
				loadingProducts.value = false;
				loadingGames.value = false
			}
		}

		watch(searchQuery, (q) => {
			if (searchQuery.value.trim() === "") {
				changeProductToGame.value = true
			}

			if (searchTimer) window.clearTimeout(searchTimer)
			searchTimer = window.setTimeout(() => {
				searchProviders(q)
			}, 350)
		})

		const handleGameStatus = async () => {
			try {
				const resp = await game.status();

				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as GameStatusResult;

				gameState.value = result;
			} catch (err: any) {
				error.value = err.message;
			}
		};

		const handleProducts = async (type: number) => {
			loadingProducts.value = true;
			error.value = null;

			try {
				const payload = { gameType: type, showGames: true };

				const resp = await game.listByType(payload);

				if (resp.code === '9902' || resp.code === '8017') {
					await auth.logout()
					window.location.reload();
				}

				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as ListByGameTypeResult;

				products.value = result.gamesList.filter(
					(product) => !isHiddenProduct(product)
				);

				selectedProduct.value = products.value[0] || null;
			} catch (err: any) {
				error.value = err.message;
			} finally {
				loadingProducts.value = false;
			}
		};

		const handleGames = async (type: number, productCode: string) => {
			try {
				loadingGames.value = true;
				error.value = null;

				const payload = { gameType: type, productCode };
				const resp = await game.listByCode(payload);

				if (resp.code === '9902' || resp.code === '8017') {
					await auth.logout()
					window.location.reload();
				}

				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as ListByGameCodeResult;
				games.value = result.gamesList.filter(
					(item) => !isHiddenProduct(item)
				);
			} catch (err: any) {
				error.value = err.message;
			} finally {
				loadingGames.value = false;
			}
		};

		const handleFavoriteAdd = async (code: string) => {
			try {
				const resp = await game.favoriteAdd(code)

				if (resp.code !== '0') throw new Error(resp.message);

				toast.add({
					severity: 'success',
					summary: resp.message,
					detail: 'เพิ่มลงรายการโปรดสำเร็จ',
					life: 3000,
				});
			} catch (err: any) {
				error.value = err.message;
			}
		}

		const handleFavoriteRemove = async (code: string) => {
			try {
				const resp = await game.favoriteDelete(code);

				if (resp.code !== '0') throw new Error(resp.message);

				toast.add({
					severity: 'success',
					summary: resp.message,
					detail: 'ลบรายการโปรดสำเร็จ',
					life: 3000,
				});
			} catch (err: any) {
				error.value = err.message;
			}
		}

		const clickProduct = (code: string) => {
			const found = products.value.find((p) => p.productCode === code)
			if (found) {
				searchQuery.value = ''
				searchResults.value = []
				selectedProduct.value = found
				changeProductToGame.value = false
			}
		}


		const clickGame = async (code: string) => {
			if (!auth.isLoggedIn) {
				await showVuePopup(Login, {}, {
					customClass: {
						popup: 'bg-[url(https://snc168.sgp1.cdn.digitaloceanspaces.com/theme/bg-login.webp)] bg-size-[110%_100%] bg-center bg-transparent',
					},
				});
				return;
			}

			const allGames = [...games.value, ...searchResults.value];
			const found = allGames.find((g) => g.loginCode === code);

			if (!found) {
				error.value = 'ไม่พบข้อมูลเกมที่เลือก';
				return;
			}

			selectedGame.value = found;

			try {
				const payload = {
					gameType: parseInt(props.gameType),
					productCode: found.productCode,
					code,
					redirect: window.location.href,
					isMobile: true
				};
				const resp = await game.login(payload);

				if (resp.code !== '0') throw new Error(resp.message);

				openGameUrl(resp?.url || window.location.href, (target) => {
					sessionStorage.setItem('go_to', target)
					window.location.href = '/goto'
				})

			} catch (err: any) {
				error.value = err.message;
			}
		};

		const clickBtnFavorite = async (code: string, actionOld: boolean) => {
			try {
				if (actionOld) {
					await handleFavoriteRemove(code);
				} else {
					await handleFavoriteAdd(code);
				}
			} catch (err: any) {
				error.value = err.message;
			} finally {
				await handleGames(gameType.value, selectedProduct.value?.productCode || '')
			}
		}

		const clickBtnBack = async () => {
			changeProductToGame.value = true
			searchQuery.value = ''
			searchResults.value = []
			await handleProducts(gameType.value)
		}


		watch(selectedProduct, (value) => {
			if (value) {
				searchQuery.value = ''
				searchResults.value = []
				handleGames(gameType.value, value.productCode)
			}
		})

		watch(() => props.gameType, async (newVal) => {
			const newGameType = parseInt(newVal);
			if (!isNaN(newGameType)) {
				gameType.value = newGameType;
				await handleProducts(newGameType);
			}
		});

		onMounted(async () => {
			await handleGameStatus();
			await handleProducts(gameType.value);
		});

		return {
			auth,
			gameType,
			productsList,
			gamesList,
			searchQuery,
			selectedProduct,
			selectedGame,
			clickProduct,
			clickGame,
			clickBtnFavorite,
			clickBtnBack,
			error,
			loadingProducts,
			loadingGames,
			changeProductToGame,
		};
	},
};
